import { useCallback, useEffect, useRef, useState } from 'react'

const COPIED_RESET_MS = 1500

/**
 * Copy-to-clipboard hook with a transient "copied" confirmation flag.
 *
 * Resilience: prefers the async Clipboard API (``navigator.clipboard``), but
 * falls back to a hidden ``<textarea>`` + ``document.execCommand('copy')`` when
 * the API is missing (insecure HTTP origins, older Safari) **or** when it
 * rejects (permission denied). Failures are swallowed and reported via the
 * returned boolean so callers can stay silent rather than alert() noisily.
 *
 * The ``copied`` flag auto-resets after ``resetMs`` and the timer is cleared on
 * unmount, so a fast unmount right after a copy never fires setState on a dead
 * component.
 */
export function useCopyToClipboard(resetMs: number = COPIED_RESET_MS) {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<number | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => clearTimer, [clearTimer])

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      if (!text) return false
      let ok = false
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text)
          ok = true
        } else {
          ok = legacyCopy(text)
        }
      } catch {
        // Async API rejected (e.g. permissions) — try the legacy path before
        // giving up so the button still works on locked-down contexts.
        ok = legacyCopy(text)
      }
      if (ok) {
        setCopied(true)
        clearTimer()
        timerRef.current = window.setTimeout(() => {
          setCopied(false)
          timerRef.current = null
        }, resetMs)
      }
      return ok
    },
    [resetMs, clearTimer],
  )

  return { copied, copy }
}

/** Synchronous clipboard fallback for non-secure contexts / no async API. */
function legacyCopy(text: string): boolean {
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'absolute'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}
